// src/pages/Reports.tsx — Multi-tab reporting dashboard (aggregates all modules)
// DATA SOURCES:
//   mockData.ts          → MOCK_JOBS, MOCK_POS, PROJECT_TYPE_META
//   mockDataExtended.ts  → MOCK_EMPLOYEES, MOCK_EQUIPMENT, getCredentialHealth, getComplianceAlerts
//   mockDataTier3.ts     → MOCK_INCIDENTS, MOCK_TOOLBOX_TALKS, MOCK_SWMS
//   mockDataCommercial.ts→ MOCK_CONTRACTS, MOCK_VARIATIONS, MOCK_PROGRESS_CLAIMS, MOCK_SCOPE_BRIEFS
import { useState } from 'react'
import { MOCK_JOBS, MOCK_POS, PROJECT_TYPE_META } from '../utils/mockData'
import { MOCK_EMPLOYEES, getCredentialHealth, MOCK_EQUIPMENT, getComplianceAlerts, MOCK_TIMESHEETS, PAY_RATES } from '../utils/mockDataExtended'
import { MOCK_INCIDENTS, MOCK_TOOLBOX_TALKS, MOCK_SWMS } from '../utils/mockDataTier3'
import { MOCK_CONTRACTS, MOCK_VARIATIONS, MOCK_PROGRESS_CLAIMS, MOCK_SCOPE_BRIEFS } from '../utils/mockDataCommercial'
import { formatCurrency, formatDate } from '../utils/formatters'
import { BarChart2, Download, TrendingUp, Shield, DollarSign, Briefcase,
  AlertTriangle, CheckCircle, Calendar, Zap, Users, FileText, ClipboardList } from 'lucide-react'
// WHS Module data
import {
  MOCK_PERMITS, MOCK_WORKER_INDUCTIONS, MOCK_WHS_INSPECTIONS, MOCK_WHS_NOTIFICATIONS,
  PERMIT_TYPE_META,
} from '../utils/mockDataWHS'

// ── Mini bar ──────────────────────────────────────────────────────
function MiniBar({ value, max, color = 'bg-blue-500' }: { value: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.min(Math.round(value / max * 100), 100) : 0
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500 w-8 text-right tabular-nums">{pct}%</span>
    </div>
  )
}

function SectionHead({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="flex items-start justify-between">
      <div>
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
        {sub && <p className="text-sm text-gray-500 mt-0.5">{sub}</p>}
      </div>
      <button className="btn-secondary flex items-center gap-1.5 text-xs"><Download className="w-3.5 h-3.5" />Export</button>
    </div>
  )
}

// ── OVERVIEW ──────────────────────────────────────────────────────
function OverviewTab() {
  const activeJobs    = MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status))
  const totalValue    = MOCK_CONTRACTS.reduce((s, c) => s + c.originalContractValue + c.approvedVariationsValue, 0)
  const retentionHeld = MOCK_CONTRACTS.reduce((s, c) => s + c.retentionHeld, 0)
  const openVarValue  = MOCK_VARIATIONS.filter(v => v.status !== 'APPROVED').reduce((s, v) => s + v.totalExGST, 0)
  const paidToDate    = MOCK_PROGRESS_CLAIMS.filter(p => p.status === 'PAID').reduce((s, p) => s + (p.certifiedAmount ?? 0), 0)
  const openIncidents = MOCK_INCIDENTS.filter(i => i.status !== 'CLOSED').length
  const credAlerts    = MOCK_EMPLOYEES.filter(e => getCredentialHealth(e) !== 'OK').length
  const plantAlerts   = getComplianceAlerts(MOCK_EQUIPMENT).length
  const pipelineValue = MOCK_SCOPE_BRIEFS.filter(b => ['RECEIVED','ESTIMATING','QUOTED','SUBMITTED'].includes(b.status)).reduce((s, b) => s + (b.estimatedValue ?? 0), 0)

  const KPI = ({ label, value, icon: Icon, color = 'bg-blue-50', textColor = 'text-blue-700', alert = false }: any) => {
    const isAlert = alert && Number(String(value).replace(/[^0-9]/g,'')) > 0
    return (
      <div className={`rounded-xl border border-gray-200 p-4 ${isAlert ? 'bg-red-50 border-red-200' : color}`}>
        <div className="flex items-start gap-3">
          <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isAlert ? 'text-red-600' : textColor}`} />
          <div>
            <div className={`text-2xl font-bold leading-none ${isAlert ? 'text-red-600' : 'text-gray-900'}`}>{value}</div>
            <div className="text-xs text-gray-500 mt-1">{label}</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <SectionHead title="Executive overview" sub={`As at ${new Date('2026-06-17').toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`} />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <KPI label="Active jobs"             value={activeJobs.length}            icon={Briefcase}     color="bg-blue-50"    textColor="text-blue-600" />
        <KPI label="Active contract value"   value={formatCurrency(totalValue)}    icon={DollarSign}    color="bg-teal-50"    textColor="text-teal-600" />
        <KPI label="Revenue received YTD"    value={formatCurrency(paidToDate)}    icon={TrendingUp}    color="bg-green-50"   textColor="text-green-600" />
        <KPI label="Retention held"          value={formatCurrency(retentionHeld)} icon={DollarSign}    color="bg-purple-50"  textColor="text-purple-600" />
        <KPI label="Open variation value"    value={formatCurrency(openVarValue)}  icon={AlertTriangle} color="bg-amber-50"   textColor="text-amber-600" />
        <KPI label="Tender pipeline"         value={formatCurrency(pipelineValue)} icon={BarChart2}     color="bg-indigo-50"  textColor="text-indigo-600" />
        <KPI label="Open HSE incidents"      value={openIncidents}                icon={Shield}        alert />
        <KPI label="Credential / plant alerts" value={credAlerts + plantAlerts}   icon={AlertTriangle} alert />
      </div>

      {/* Jobs by type */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Portfolio by project type</h3>
        <div className="space-y-3">
          {['SIGNALS','CIVIL','ENERGY_GRID'].map(type => {
            const jobs  = MOCK_JOBS.filter(j => j.type === type)
            const value = jobs.reduce((s, j) => s + j.contractValue, 0)
            const meta  = PROJECT_TYPE_META[type]
            return (
              <div key={type} className="flex items-center gap-4">
                <div className="w-32 text-sm text-gray-700 font-medium">{meta.label}</div>
                <MiniBar value={jobs.length} max={MOCK_JOBS.length} color={type === 'SIGNALS' ? 'bg-blue-500' : type === 'CIVIL' ? 'bg-teal-500' : 'bg-amber-500'} />
                <div className="w-12 text-xs text-gray-500 text-center">{jobs.length} jobs</div>
                <div className="w-28 text-sm font-medium text-right tabular-nums">{formatCurrency(value)}</div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Jobs progress */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b bg-gray-50"><h3 className="font-semibold text-gray-900 text-sm">Job progress summary</h3></div>
        <div className="divide-y divide-gray-100">
          {MOCK_JOBS.map(j => {
            const meta = PROJECT_TYPE_META[j.type] ?? PROJECT_TYPE_META.SIGNALS
            return (
              <div key={j.id} className="flex items-center gap-3 px-5 py-3">
                <span className="text-xs font-mono text-gray-500 w-28 flex-shrink-0">{j.jobNumber}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${meta.bg} ${meta.color}`}>{meta.label}</span>
                <div className="flex-1 min-w-0"><MiniBar value={j.progressPercent} max={100} color={j.progressPercent >= 75 ? 'bg-green-500' : j.progressPercent >= 40 ? 'bg-blue-500' : 'bg-gray-400'} /></div>
                <span className="text-xs text-gray-500 w-8 flex-shrink-0 tabular-nums">{j.progressPercent}%</span>
                <span className="text-sm font-medium w-24 text-right tabular-nums">{formatCurrency(j.contractValue)}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ── FINANCIAL ─────────────────────────────────────────────────────
function FinancialTab() {
  const totalContractValue = MOCK_CONTRACTS.reduce((s, c) => s + c.originalContractValue + c.approvedVariationsValue, 0)
  const totalRetention     = MOCK_CONTRACTS.reduce((s, c) => s + c.retentionHeld, 0)
  const totalVarApproved   = MOCK_VARIATIONS.filter(v => v.status === 'APPROVED').reduce((s, v) => s + v.totalExGST, 0)
  const totalVarPending    = MOCK_VARIATIONS.filter(v => v.status !== 'APPROVED').reduce((s, v) => s + v.totalExGST, 0)
  const totalPaid          = MOCK_PROGRESS_CLAIMS.filter(p => p.status === 'PAID').reduce((s, p) => s + (p.certifiedAmount ?? 0), 0)
  const totalCertified     = MOCK_PROGRESS_CLAIMS.filter(p => p.status === 'CERTIFIED').reduce((s, p) => s + (p.certifiedAmount ?? 0), 0)
  const totalOpenPOs       = MOCK_POS.filter(p => !['DELIVERED','REJECTED'].includes(p.status)).reduce((s, p) => s + p.totalExGST, 0)

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Financial report</h2>
          <p className="text-sm text-gray-500 mt-0.5">Contracts, variations, claims, and procurement</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => generateJobCostReport(MOCK_JOBS, MOCK_POS, MOCK_TIMESHEETS, PAY_RATES, MOCK_EMPLOYEES)} className="btn-secondary flex items-center gap-1.5 text-xs">
            <Download className="w-3.5 h-3.5" />Job cost report
          </button>
          <button onClick={() => generateAgedReceivablesReport(MOCK_PROGRESS_CLAIMS)} className="btn-secondary flex items-center gap-1.5 text-xs">
            <Download className="w-3.5 h-3.5" />Aged receivables
          </button>
          <button onClick={() => generateRetentionScheduleReport(MOCK_CONTRACTS)} className="btn-secondary flex items-center gap-1.5 text-xs">
            <Download className="w-3.5 h-3.5" />Retention schedule
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Active contract value',       value: formatCurrency(totalContractValue), bg: 'bg-blue-50',   text: 'text-blue-700' },
          { label: 'Revenue received (paid)',      value: formatCurrency(totalPaid),          bg: 'bg-green-50',  text: 'text-green-700' },
          { label: 'Certified — awaiting payment', value: formatCurrency(totalCertified),     bg: 'bg-amber-50',  text: 'text-amber-700' },
          { label: 'Retention held (all)',         value: formatCurrency(totalRetention),      bg: 'bg-purple-50', text: 'text-purple-700' },
        ].map(c => (
          <div key={c.label} className={`rounded-xl border border-gray-200 p-4 ${c.bg}`}>
            <div className={`text-xl font-bold ${c.text}`}>{c.value}</div>
            <div className="text-xs text-gray-500 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Contract table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b bg-gray-50"><h3 className="font-semibold text-gray-900 text-sm">Contract register summary</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-gray-500 bg-gray-50 border-b">
              <th className="text-left px-4 py-2.5 font-medium">Job</th>
              <th className="text-left px-3 py-2.5 font-medium">Client</th>
              <th className="text-left px-3 py-2.5 font-medium">Type</th>
              <th className="text-right px-3 py-2.5 font-medium">Original</th>
              <th className="text-right px-3 py-2.5 font-medium">Variations</th>
              <th className="text-right px-3 py-2.5 font-medium">Current</th>
              <th className="text-right px-4 py-2.5 font-medium">Retention</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-50">
              {MOCK_CONTRACTS.map(c => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-900">
                    <button onClick={() => generateMonthlyProgressReport(MOCK_JOBS.find(j => j.id === c.jobId), c, MOCK_PROGRESS_CLAIMS, MOCK_VARIATIONS)}
                      className="hover:underline hover:text-indigo-700 transition-colors" title="Generate monthly progress report">
                      {c.jobNumber}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-gray-600 text-xs">{c.clientName}</td>
                  <td className="px-3 py-2.5 text-xs font-medium text-gray-500">{c.contractType}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{formatCurrency(c.originalContractValue)}</td>
                  <td className={`px-3 py-2.5 text-right tabular-nums ${c.approvedVariationsValue > 0 ? 'text-blue-700 font-medium' : 'text-gray-400'}`}>{c.approvedVariationsValue > 0 ? `+${formatCurrency(c.approvedVariationsValue)}` : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-bold">{formatCurrency(c.originalContractValue + c.approvedVariationsValue)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-purple-700">{formatCurrency(c.retentionHeld)}</td>
                </tr>
              ))}
              <tr className="border-t-2 bg-gray-50 font-bold">
                <td colSpan={3} className="px-4 py-2.5 text-gray-700">Total</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatCurrency(MOCK_CONTRACTS.reduce((s,c) => s+c.originalContractValue,0))}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-blue-700">+{formatCurrency(totalVarApproved)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-base">{formatCurrency(totalContractValue)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-purple-700">{formatCurrency(totalRetention)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="font-semibold text-gray-900 text-sm mb-3">Variation summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-600">Approved</span><span className="font-medium text-green-700">{formatCurrency(totalVarApproved)}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Submitted / pending</span><span className="font-medium text-amber-700">{formatCurrency(totalVarPending)}</span></div>
            <div className="flex justify-between border-t pt-2"><span className="font-semibold">Total variation value</span><span className="font-bold">{formatCurrency(totalVarApproved + totalVarPending)}</span></div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="font-semibold text-gray-900 text-sm mb-3">Open PO commitment</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-600">Open commitment</span><span className="font-bold">{formatCurrency(totalOpenPOs)}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Orders in transit</span><span>{MOCK_POS.filter(p => !['DELIVERED','REJECTED'].includes(p.status)).length} orders</span></div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── HSE ──────────────────────────────────────────────────────────
function HSETab() {
  const byType      = MOCK_INCIDENTS.reduce((acc, i) => { acc[i.type] = (acc[i.type] ?? 0) + 1; return acc }, {} as Record<string,number>)
  const bySeverity  = MOCK_INCIDENTS.reduce((acc, i) => { acc[i.severity] = (acc[i.severity] ?? 0) + 1; return acc }, {} as Record<string,number>)
  const expiring    = MOCK_EMPLOYEES.filter(e => getCredentialHealth(e) === 'EXPIRING').length
  const expired     = MOCK_EMPLOYEES.filter(e => getCredentialHealth(e) === 'EXPIRED').length
  const plantAlerts = getComplianceAlerts(MOCK_EQUIPMENT)

  const typeLabels: Record<string,string>     = { INCIDENT:'Incident', NEAR_MISS:'Near miss', HAZARD_OBSERVATION:'Hazard observation', PROPERTY_DAMAGE:'Property damage' }
  const severityColors: Record<string,string> = { LOW:'bg-blue-500', MEDIUM:'bg-amber-500', HIGH:'bg-orange-500', CRITICAL:'bg-red-600' }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">HSE report</h2>
          <p className="text-sm text-gray-500 mt-0.5">Incidents, credentials, SWMS, and plant compliance</p>
        </div>
        <button onClick={() => generateWHSComplianceReport(MOCK_INCIDENTS, MOCK_TOOLBOX_TALKS, MOCK_SWMS, MOCK_EMPLOYEES, MOCK_WHS_INSPECTIONS)}
          className="btn-secondary flex items-center gap-1.5 text-xs">
          <Download className="w-3.5 h-3.5" />WHS compliance report
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total incidents YTD', value: MOCK_INCIDENTS.length, bg: 'bg-gray-50' },
          { label: 'Open / under investigation', value: MOCK_INCIDENTS.filter(i=>i.status!=='CLOSED').length, bg: MOCK_INCIDENTS.filter(i=>i.status!=='CLOSED').length>0 ? 'bg-red-50' : 'bg-gray-50' },
          { label: 'Toolbox talks YTD',   value: MOCK_TOOLBOX_TALKS.length, bg: 'bg-green-50' },
          { label: 'SWMS approved',       value: MOCK_SWMS.filter(s=>s.status==='APPROVED').length, bg: 'bg-blue-50' },
        ].map(c => (
          <div key={c.label} className={`rounded-xl border border-gray-200 p-4 ${c.bg}`}>
            <div className="text-2xl font-bold text-gray-900">{c.value}</div>
            <div className="text-xs text-gray-500 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="font-semibold text-gray-900 text-sm mb-3">By type</h3>
          <div className="space-y-2">
            {Object.entries(byType).map(([type, count]) => (
              <div key={type} className="flex items-center gap-3">
                <span className="text-xs text-gray-600 w-36">{typeLabels[type] ?? type}</span>
                <MiniBar value={count} max={MOCK_INCIDENTS.length} color="bg-blue-500" />
                <span className="text-xs font-bold text-gray-900 w-4">{count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="font-semibold text-gray-900 text-sm mb-3">By severity</h3>
          <div className="space-y-2">
            {['CRITICAL','HIGH','MEDIUM','LOW'].filter(s => bySeverity[s]).map(sev => (
              <div key={sev} className="flex items-center gap-3">
                <span className="text-xs text-gray-600 w-16">{sev}</span>
                <MiniBar value={bySeverity[sev]} max={MOCK_INCIDENTS.length} color={severityColors[sev]} />
                <span className="text-xs font-bold text-gray-900 w-4">{bySeverity[sev]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Credential health */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 text-sm">Employee credential health</h3>
          {(expired + expiring) > 0 && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{expired + expiring} alerts</span>}
        </div>
        <div className="divide-y divide-gray-100">
          {MOCK_EMPLOYEES.map(emp => {
            const health = getCredentialHealth(emp)
            return (
              <div key={emp.id} className={`flex items-center gap-3 px-5 py-2.5 ${health === 'EXPIRED' ? 'bg-red-50' : health === 'EXPIRING' ? 'bg-amber-50/50' : ''}`}>
                <span className="text-sm font-medium text-gray-900 w-36">{emp.fullName}</span>
                <span className="text-xs text-gray-500 flex-1">{emp.payClassification.replace(/_/g,' ')}</span>
                <span className={`text-xs font-bold ${health === 'EXPIRED' ? 'text-red-700' : health === 'EXPIRING' ? 'text-amber-700' : 'text-green-700'}`}>
                  {health === 'OK' ? '✓ OK' : health === 'EXPIRING' ? '⚠ Expiring' : '✕ Expired'}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Plant compliance */}
      {plantAlerts.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-900 text-sm">Plant &amp; equipment compliance alerts ({plantAlerts.length})</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {plantAlerts.slice(0, 6).map(a => (
              <div key={a.id} className={`flex items-center gap-3 px-5 py-2.5 ${a.severity === 'HIGH' ? 'bg-red-50' : 'bg-amber-50/50'}`}>
                <span className="text-sm font-mono text-gray-500 w-20">{a.assetNumber}</span>
                <span className="text-sm text-gray-700 flex-1">{a.type}</span>
                <span className="text-xs text-gray-400">{formatDate(a.dueDate)}</span>
                <span className={`text-xs font-bold ${a.daysLeft <= 0 ? 'text-red-700' : a.severity === 'HIGH' ? 'text-red-600' : 'text-amber-600'}`}>
                  {a.daysLeft <= 0 ? 'OVERDUE' : `${a.daysLeft}d`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── PROGRAMME ─────────────────────────────────────────────────────
function ProgrammeTab() {
  return (
    <div className="space-y-6">
      <SectionHead title="Programme report" sub="Job progress and resource utilisation" />

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b bg-gray-50"><h3 className="font-semibold text-gray-900 text-sm">All jobs — progress and schedule</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-gray-500 bg-gray-50 border-b">
              <th className="text-left px-4 py-2.5 font-medium">Job</th>
              <th className="text-left px-3 py-2.5 font-medium">Type</th>
              <th className="text-left px-3 py-2.5 font-medium">Status</th>
              <th className="text-left px-3 py-2.5 font-medium w-40">Progress</th>
              <th className="text-left px-3 py-2.5 font-medium">Start</th>
              <th className="text-left px-3 py-2.5 font-medium">Target PC</th>
              <th className="text-right px-4 py-2.5 font-medium">Crew</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-50">
              {MOCK_JOBS.map(j => {
                const meta     = PROJECT_TYPE_META[j.type] ?? PROJECT_TYPE_META.SIGNALS
                const daysLeft = j.targetEnd ? Math.ceil((new Date(j.targetEnd).getTime() - new Date('2026-06-17').getTime()) / 86400000) : null
                return (
                  <tr key={j.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{j.jobNumber}</td>
                    <td className="px-3 py-3"><span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${meta.bg} ${meta.color}`}>{meta.label}</span></td>
                    <td className="px-3 py-3 text-xs text-gray-500">{j.status.replace('_',' ')}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${j.progressPercent >= 75 ? 'bg-green-500' : j.progressPercent >= 40 ? 'bg-blue-500' : 'bg-gray-400'}`} style={{ width: `${j.progressPercent}%` }} />
                        </div>
                        <span className="text-xs tabular-nums">{j.progressPercent}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-500">{j.startDate ? formatDate(j.startDate) : '—'}</td>
                    <td className="px-3 py-3 text-xs">
                      {j.targetEnd ? (
                        <span className={daysLeft !== null && daysLeft <= 21 ? (daysLeft <= 7 ? 'text-red-600 font-bold' : 'text-amber-600') : 'text-gray-500'}>
                          {formatDate(j.targetEnd)}{daysLeft !== null && daysLeft > 0 ? ` (${daysLeft}d)` : daysLeft !== null && daysLeft <= 0 ? ' OVERDUE' : ''}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium">{j.crew}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 text-sm mb-4">Portfolio workforce — current week</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          {[
            { label: 'Total crew on site',  value: MOCK_JOBS.reduce((s,j) => s + j.crew, 0) },
            { label: 'Total plant on site', value: MOCK_JOBS.reduce((s,j) => s + j.plantOnSite, 0) },
            { label: 'Open HSE issues',     value: MOCK_JOBS.reduce((s,j) => s + j.openIssues, 0) },
            { label: 'Open hold points',    value: MOCK_JOBS.reduce((s,j) => s + j.openHoldPoints, 0) },
          ].map(f => (
            <div key={f.label}>
              <div className="text-2xl font-bold text-gray-900">{f.value}</div>
              <div className="text-xs text-gray-500 mt-1">{f.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}


// ─── GST formatting helpers ────────────────────────────────────────
// fmtX: plain $ amount (no AUD prefix — assumed AUD for all documents)
// gstRow: returns three table rows for ex-GST / GST / total presentation
function fmtX(n: number): string {
  return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}
function gstRows(exGST: number, label = 'Amount'): string {
  const gst   = exGST * 0.1
  const total = exGST + gst
  return `
    <tr style="border-top:1px solid #e5e7eb">
      <td colspan="999" style="padding:2px"></td>
    </tr>
    <tr>
      <td colspan="2" style="padding:5px 10px;text-align:right;font-size:9.5pt;color:#555">${label} (ex GST)</td>
      <td style="padding:5px 10px;text-align:right;font-family:monospace">${fmtX(exGST)}</td>
    </tr>
    <tr>
      <td colspan="2" style="padding:5px 10px;text-align:right;font-size:9.5pt;color:#555">GST (10%)</td>
      <td style="padding:5px 10px;text-align:right;font-family:monospace">${fmtX(gst)}</td>
    </tr>
    <tr style="background:#f0f9ff">
      <td colspan="2" style="padding:6px 10px;text-align:right;font-weight:bold">Total (inc GST)</td>
      <td style="padding:6px 10px;text-align:right;font-weight:bold;font-family:monospace">${fmtX(total)}</td>
    </tr>`
}

// ─────────────────────────────────────────────────────────────────
// WHS ANALYTICS TAB
// Data sources: all 4 WHS modules built in Modules 1-4
// ─────────────────────────────────────────────────────────────────

// Helper: days between two dates
function daysBetween(a: string, b: string) {
  return Math.ceil((new Date(b).getTime() - new Date(a).getTime()) / (1000 * 60 * 60 * 24))
}

// Inline radial-like progress ring using SVG (no external chart lib)
function RingKPI({ value, max, label, sublabel, color = '#4f46e5' }: {
  value: number; max: number; label: string; sublabel: string; color?: string
}) {
  const pct = max > 0 ? Math.min(value / max, 1) : 0
  const r = 28; const circ = 2 * Math.PI * r
  const offset = circ * (1 - pct)
  return (
    <div className="flex flex-col items-center gap-2">
      <svg width="72" height="72" viewBox="0 0 72 72">
        <circle cx="36" cy="36" r={r} fill="none" stroke="#f1f5f9" strokeWidth="7" />
        <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="7"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 36 36)" />
        <text x="36" y="40" textAnchor="middle" fontSize="13" fontWeight="700" fill="#0f172a">
          {max > 0 ? `${Math.round(pct * 100)}%` : '—'}
        </text>
      </svg>
      <div className="text-center">
        <p className="text-xs font-semibold text-slate-700 leading-tight">{label}</p>
        <p className="text-xs text-slate-400 mt-0.5">{sublabel}</p>
      </div>
    </div>
  )
}

// Stacked bar using CSS flex
function StackedBar({ segments }: { segments: { count: number; total: number; color: string; label: string }[] }) {
  const total = segments.reduce((s, seg) => s + seg.count, 0)
  if (total === 0) return <div className="h-3 bg-slate-100 rounded-full" />
  return (
    <div className="flex h-3 rounded-full overflow-hidden gap-px">
      {segments.filter(s => s.count > 0).map((seg, i) => (
        <div key={i} className={`${seg.color} transition-all`}
          style={{ width: `${(seg.count / total) * 100}%` }}
          title={`${seg.label}: ${seg.count}`} />
      ))}
    </div>
  )
}

// AI-style insight card
function InsightCard({ type, title, body, action }: {
  type: 'risk' | 'info' | 'good'; title: string; body: string; action?: string
}) {
  const meta = {
    risk: { bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500', badge: 'Risk flag', badgeCls: 'bg-red-100 text-red-700' },
    info: { bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-500', badge: 'Observation', badgeCls: 'bg-blue-100 text-blue-700' },
    good: { bg: 'bg-green-50', border: 'border-green-200', dot: 'bg-green-500', badge: 'Positive trend', badgeCls: 'bg-green-100 text-green-700' },
  }[type]
  return (
    <div className={`rounded-xl border p-4 ${meta.bg} ${meta.border}`}>
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center gap-1 pt-0.5 flex-shrink-0">
          <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${meta.badgeCls}`}>
              ✦ AI · {meta.badge}
            </span>
          </div>
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          <p className="text-xs text-slate-600 mt-1 leading-relaxed">{body}</p>
          {action && (
            <p className="text-xs font-medium text-indigo-700 mt-2">
              → Recommended action: {action}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

function WHSAnalyticsTab() {
  // ── Permits ─────────────────────────────────────────────────────
  const totalPermits    = MOCK_PERMITS.length
  const permitByStatus  = MOCK_PERMITS.reduce((acc, p) => { acc[p.status] = (acc[p.status] ?? 0) + 1; return acc }, {} as Record<string, number>)
  const permitByType    = MOCK_PERMITS.reduce((acc, p) => { acc[p.type] = (acc[p.type] ?? 0) + 1; return acc }, {} as Record<string, number>)
  const pendingPermits  = (permitByStatus['SUBMITTED'] ?? 0)
  const activePermits   = (permitByStatus['IN_PROGRESS'] ?? 0)
  const completedPermits= (permitByStatus['COMPLETED'] ?? 0)

  // ── Inductions ──────────────────────────────────────────────────
  const totalInductions = MOCK_WORKER_INDUCTIONS.length
  const passedI         = MOCK_WORKER_INDUCTIONS.filter(w => w.status === 'PASSED').length
  const failedI         = MOCK_WORKER_INDUCTIONS.filter(w => w.status === 'FAILED').length
  const pendingI        = MOCK_WORKER_INDUCTIONS.filter(w => w.status === 'PENDING' || w.status === 'IN_PROGRESS').length
  const expiringI       = MOCK_WORKER_INDUCTIONS.filter(w => {
    if (!w.expiryDate) return false
    const days = Math.ceil((new Date(w.expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    return days >= 0 && days <= 30
  }).length

  // ── Inspections ─────────────────────────────────────────────────
  const totalInspections = MOCK_WHS_INSPECTIONS.length
  const passedInsp       = MOCK_WHS_INSPECTIONS.filter(i => i.result === 'PASS').length
  const conditionalInsp  = MOCK_WHS_INSPECTIONS.filter(i => i.result === 'CONDITIONAL').length
  const failedInsp       = MOCK_WHS_INSPECTIONS.filter(i => i.result === 'FAIL').length
  const allIssues        = MOCK_WHS_INSPECTIONS.flatMap(i => i.issues)
  const openIssues       = allIssues.filter(i => i.status !== 'CLOSED')
  const closedIssues     = allIssues.filter(i => i.status === 'CLOSED')
  const issueCloseRate   = allIssues.length > 0 ? Math.round((closedIssues.length / allIssues.length) * 100) : 100
  const issuesBySeverity = openIssues.reduce((acc, i) => { acc[i.severity] = (acc[i.severity] ?? 0) + 1; return acc }, {} as Record<string, number>)

  // ── Incidents (from existing module) ────────────────────────────
  const totalIncidents   = MOCK_INCIDENTS.length
  const ltiCount         = MOCK_INCIDENTS.filter(i => i.treatmentRequired === 'HOSPITAL').length
  const recordable       = MOCK_INCIDENTS.filter(i => ['MEDICAL_TREATMENT','HOSPITAL'].includes(i.treatmentRequired ?? '')).length
  // Estimated hours: 8 employees × 9hr/day × 5 days × 11 weeks YTD ≈ 3,960
  const estHoursWorked   = 3960
  const TRIFR            = recordable > 0 ? ((recordable / estHoursWorked) * 1_000_000).toFixed(1) : '0.0'
  const LTIFR            = ltiCount > 0   ? ((ltiCount   / estHoursWorked) * 1_000_000).toFixed(1) : '0.0'
  // Days since last incident
  const incidentDates = MOCK_INCIDENTS.map(i => i.dateTime).sort()
  const lastIncidentDate = incidentDates.length > 0
    ? incidentDates[incidentDates.length - 1]
    : null
  const daysSinceLast    = lastIncidentDate ? daysBetween(lastIncidentDate, new Date().toISOString()) : null

  // ── Toolbox talks ───────────────────────────────────────────────
  const thisMonth  = new Date().toISOString().slice(0, 7)
  const talksMonth = MOCK_TOOLBOX_TALKS.filter(t => t.date.startsWith(thisMonth)).length

  // ── Notifications ───────────────────────────────────────────────
  const urgentNotifs = MOCK_WHS_NOTIFICATIONS.filter(n => n.severity === 'URGENT').length

  // ── Permit type colours ─────────────────────────────────────────
  const permitTypeColors: Record<string, string> = {
    ENERGISED_ELECTRICAL: 'bg-yellow-500', EXCAVATION: 'bg-orange-500',
    CONFINED_SPACE: 'bg-purple-500', HOT_WORKS: 'bg-red-500',
    WORKING_AT_HEIGHTS: 'bg-blue-500', TRAFFIC_MANAGEMENT: 'bg-teal-500',
    OVERHEAD_LINES: 'bg-amber-500', LOTO: 'bg-slate-500',
  }

  return (
    <div className="space-y-8">
      <SectionHead
        title="WHS Analytics"
        sub={`Live safety performance dashboard — as at ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`}
      />

      {/* ── TOP-LINE KPIs ──────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          {
            label: 'Days without LTI', icon: '🏆',
            value: daysSinceLast !== null ? String(daysSinceLast) : '—',
            bg: 'bg-green-50', text: 'text-green-700', sub: 'Last incident Jun 15',
          },
          {
            label: 'TRIFR', icon: '📊',
            value: TRIFR,
            bg: Number(TRIFR) > 10 ? 'bg-red-50' : 'bg-green-50',
            text: Number(TRIFR) > 10 ? 'text-red-700' : 'text-green-700',
            sub: 'per million hrs',
          },
          {
            label: 'LTIFR', icon: '📉',
            value: LTIFR,
            bg: Number(LTIFR) > 0 ? 'bg-red-50' : 'bg-green-50',
            text: Number(LTIFR) > 0 ? 'text-red-700' : 'text-green-700',
            sub: 'per million hrs',
          },
          {
            label: 'Permits to Work', icon: '🔐',
            value: `${activePermits} active`,
            bg: 'bg-indigo-50', text: 'text-indigo-700',
            sub: `${pendingPermits} awaiting approval`,
          },
          {
            label: 'Induction rate', icon: '🎓',
            value: `${totalInductions > 0 ? Math.round((passedI / totalInductions) * 100) : 0}%`,
            bg: passedI / totalInductions < 0.8 ? 'bg-amber-50' : 'bg-green-50',
            text: passedI / totalInductions < 0.8 ? 'text-amber-700' : 'text-green-700',
            sub: `${passedI}/${totalInductions} workers`,
          },
          {
            label: 'Open issues', icon: '⚠',
            value: String(openIssues.length),
            bg: openIssues.length > 0 ? 'bg-red-50' : 'bg-green-50',
            text: openIssues.length > 0 ? 'text-red-700' : 'text-green-700',
            sub: `${issueCloseRate}% close-out rate`,
          },
        ].map(k => (
          <div key={k.label} className={`rounded-xl border border-white p-4 ${k.bg}`}>
            <div className="text-lg mb-1">{k.icon}</div>
            <p className={`text-2xl font-bold ${k.text}`}>{k.value}</p>
            <p className="text-xs text-slate-500 mt-0.5 leading-tight">{k.label}</p>
            <p className="text-xs text-slate-400 mt-0.5">{k.sub}</p>
          </div>
        ))}
      </div>

      {/* ── PERMITS TO WORK ─────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5">
        <h3 className="font-semibold text-slate-900">Permits to Work — breakdown</h3>

        {/* Status distribution */}
        <div>
          <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
            <span>Status distribution ({totalPermits} total)</span>
            <div className="flex items-center gap-3">
              {[
                { label: 'Submitted', color: 'bg-blue-500' },
                { label: 'Approved', color: 'bg-green-500' },
                { label: 'In Progress', color: 'bg-indigo-500' },
                { label: 'Completed', color: 'bg-slate-400' },
              ].map(l => (
                <span key={l.label} className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${l.color}`} />{l.label}
                </span>
              ))}
            </div>
          </div>
          <StackedBar segments={[
            { count: permitByStatus['SUBMITTED']   ?? 0, total: totalPermits, color: 'bg-blue-500',   label: 'Submitted' },
            { count: permitByStatus['APPROVED']    ?? 0, total: totalPermits, color: 'bg-green-500',  label: 'Approved' },
            { count: permitByStatus['IN_PROGRESS'] ?? 0, total: totalPermits, color: 'bg-indigo-500', label: 'In Progress' },
            { count: permitByStatus['COMPLETED']   ?? 0, total: totalPermits, color: 'bg-slate-300',  label: 'Completed' },
            { count: permitByStatus['REJECTED']    ?? 0, total: totalPermits, color: 'bg-red-400',    label: 'Rejected' },
          ]} />
        </div>

        {/* By permit type */}
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Volume by permit type</p>
          <div className="space-y-2">
            {Object.entries(permitByType).sort((a, b) => b[1] - a[1]).map(([type, count]) => {
              const meta = PERMIT_TYPE_META[type as keyof typeof PERMIT_TYPE_META]
              return (
                <div key={type} className="flex items-center gap-3">
                  <span className="text-base w-6 flex-shrink-0">{meta?.icon ?? '🔐'}</span>
                  <span className="text-xs text-slate-600 w-44 flex-shrink-0 truncate">{meta?.label ?? type}</span>
                  <div className="flex-1">
                    <MiniBar value={count} max={totalPermits} color={permitTypeColors[type] ?? 'bg-indigo-500'} />
                  </div>
                  <span className="text-xs font-bold text-slate-700 w-4 text-right">{count}</span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Permit status table */}
        <div className="grid grid-cols-5 gap-2">
          {[
            { status: 'SUBMITTED',   label: 'Pending approval', color: 'text-blue-600',   bg: 'bg-blue-50' },
            { status: 'APPROVED',    label: 'Approved',         color: 'text-green-600',  bg: 'bg-green-50' },
            { status: 'IN_PROGRESS', label: 'On site now',      color: 'text-indigo-600', bg: 'bg-indigo-50' },
            { status: 'COMPLETED',   label: 'Completed',        color: 'text-slate-600',  bg: 'bg-slate-100' },
            { status: 'REJECTED',    label: 'Rejected',         color: 'text-red-600',    bg: 'bg-red-50' },
          ].map(s => (
            <div key={s.status} className={`rounded-lg ${s.bg} p-3 text-center`}>
              <p className={`text-xl font-bold ${s.color}`}>{permitByStatus[s.status] ?? 0}</p>
              <p className="text-xs text-slate-400 mt-0.5 leading-tight">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── INDUCTIONS ──────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-slate-900">Site Inductions</h3>
          {expiringI > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full font-medium">
              ⚠ {expiringI} expiring within 30 days
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
          {[
            { label: 'Passed',  value: passedI,  color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Pending', value: pendingI,  color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'Failed',  value: failedI,  color: 'text-red-600',   bg: 'bg-red-50' },
            { label: 'Expiring ≤30d', value: expiringI, color: 'text-orange-600', bg: 'bg-orange-50' },
          ].map(s => (
            <div key={s.label} className={`rounded-lg ${s.bg} p-3 text-center`}>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-slate-400 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Per-project induction rates */}
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Completion rate by project</p>
        <div className="space-y-3">
          {Array.from(new Set(MOCK_WORKER_INDUCTIONS.filter(w => w.jobId).map(w => w.jobId!))).map(jobId => {
            const job       = MOCK_JOBS.find(j => j.id === jobId)
            const jobInds   = MOCK_WORKER_INDUCTIONS.filter(w => w.jobId === jobId)
            const jobPassed = jobInds.filter(w => w.status === 'PASSED').length
            return (
              <div key={jobId} className="flex items-center gap-3">
                <span className="text-xs font-mono text-slate-500 w-28 flex-shrink-0">{job?.jobNumber ?? jobId}</span>
                <div className="flex-1">
                  <MiniBar value={jobPassed} max={jobInds.length} color={jobPassed / jobInds.length >= 0.9 ? 'bg-green-500' : jobPassed / jobInds.length >= 0.6 ? 'bg-amber-500' : 'bg-red-500'} />
                </div>
                <span className="text-xs text-slate-500 w-16 text-right flex-shrink-0">
                  {jobPassed}/{jobInds.length} passed
                </span>
              </div>
            )
          })}
          {/* Company-wide inductions (no jobId) */}
          {(() => {
            const companyInds = MOCK_WORKER_INDUCTIONS.filter(w => !w.jobId)
            const passed = companyInds.filter(w => w.status === 'PASSED').length
            if (companyInds.length === 0) return null
            return (
              <div className="flex items-center gap-3">
                <span className="text-xs font-mono text-slate-500 w-28 flex-shrink-0">Company-wide</span>
                <div className="flex-1">
                  <MiniBar value={passed} max={companyInds.length} color="bg-indigo-500" />
                </div>
                <span className="text-xs text-slate-500 w-16 text-right flex-shrink-0">
                  {passed}/{companyInds.length} passed
                </span>
              </div>
            )
          })()}
        </div>
      </div>

      {/* ── INSPECTIONS & ISSUES ─────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Inspection results */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-semibold text-slate-900 mb-4">Inspections</h3>
          <div className="flex items-end gap-4 mb-4">
            <div>
              <p className="text-3xl font-bold text-slate-900">{totalInspections}</p>
              <p className="text-xs text-slate-400">total conducted</p>
            </div>
            <div className="flex-1">
              <StackedBar segments={[
                { count: passedInsp,      total: totalInspections, color: 'bg-green-500', label: 'Pass' },
                { count: conditionalInsp, total: totalInspections, color: 'bg-amber-400', label: 'Conditional' },
                { count: failedInsp,      total: totalInspections, color: 'bg-red-500',   label: 'Fail' },
              ]} />
              <div className="flex items-center gap-3 mt-2">
                {[
                  { label: 'Pass', count: passedInsp, color: 'bg-green-500' },
                  { label: 'Conditional', count: conditionalInsp, color: 'bg-amber-400' },
                  { label: 'Fail', count: failedInsp, color: 'bg-red-500' },
                ].map(s => (
                  <span key={s.label} className="flex items-center gap-1 text-xs text-slate-500">
                    <span className={`w-2 h-2 rounded-full ${s.color}`} />{s.label}: {s.count}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Inspection types conducted */}
          {(() => {
            const byType = MOCK_WHS_INSPECTIONS.reduce((acc, i) => {
              const labels: Record<string, string> = {
                DAILY_PRESTART: 'Daily pre-start', SAFETY_WALK: 'Safety walk',
                PLANT_INSPECTION: 'Plant', ELECTRICAL_INSTALL: 'Electrical',
                EXCAVATION_CHECK: 'Excavation', CONFINED_SPACE_PREENTRY: 'Confined space',
                WEEKLY_SITE_AUDIT: 'Weekly audit',
              }
              const k = labels[i.type] ?? i.type
              acc[k] = (acc[k] ?? 0) + 1
              return acc
            }, {} as Record<string, number>)
            return (
              <div className="space-y-1.5 mt-2 pt-2 border-t border-slate-100">
                {Object.entries(byType).map(([type, count]) => (
                  <div key={type} className="flex items-center gap-2 text-xs">
                    <span className="text-slate-500 flex-1">{type}</span>
                    <span className="font-bold text-slate-700">{count}</span>
                  </div>
                ))}
              </div>
            )
          })()}
        </div>

        {/* Open issues */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Open Issues</h3>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full
              ${openIssues.length === 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {openIssues.length} open
            </span>
          </div>

          {/* By severity */}
          <div className="space-y-2 mb-4">
            {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(sev => {
              const count = issuesBySeverity[sev] ?? 0
              const colors: Record<string, string> = {
                CRITICAL: 'bg-red-500', HIGH: 'bg-orange-500', MEDIUM: 'bg-amber-400', LOW: 'bg-blue-400'
              }
              return (
                <div key={sev} className="flex items-center gap-3">
                  <span className="text-xs text-slate-500 w-16">{sev}</span>
                  <div className="flex-1">
                    <MiniBar value={count} max={Math.max(openIssues.length, 1)} color={colors[sev]} />
                  </div>
                  <span className="text-xs font-bold text-slate-700 w-4 text-right">{count}</span>
                </div>
              )
            })}
          </div>

          {/* Close-out rate */}
          <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400">Close-out rate</p>
              <p className={`text-2xl font-bold mt-0.5 ${issueCloseRate >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
                {issueCloseRate}%
              </p>
              <p className="text-xs text-slate-400">{closedIssues.length}/{allIssues.length} issues resolved</p>
            </div>
            <div className="w-20">
              <RingKPI
                value={issueCloseRate}
                max={100}
                label=""
                sublabel=""
                color={issueCloseRate >= 80 ? '#22c55e' : '#f59e0b'}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── COMPLETION RATES RING CHART ──────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-semibold text-slate-900 mb-5">WHS compliance rates — at a glance</h3>
        <div className="flex items-center justify-around flex-wrap gap-6">
          <RingKPI
            value={passedI} max={totalInductions}
            label="Induction rate"
            sublabel={`${passedI}/${totalInductions} workers`}
            color={passedI / Math.max(totalInductions, 1) >= 0.9 ? '#22c55e' : '#f59e0b'}
          />
          <RingKPI
            value={completedPermits + activePermits + (permitByStatus['APPROVED'] ?? 0)}
            max={totalPermits}
            label="Permit compliance"
            sublabel={`${totalPermits - pendingPermits} of ${totalPermits}`}
            color="#4f46e5"
          />
          <RingKPI
            value={passedInsp} max={Math.max(totalInspections, 1)}
            label="Inspection pass rate"
            sublabel={`${passedInsp}/${totalInspections} passed`}
            color={passedInsp / Math.max(totalInspections, 1) >= 0.7 ? '#22c55e' : '#f59e0b'}
          />
          <RingKPI
            value={issueCloseRate} max={100}
            label="Issue close-out rate"
            sublabel={`${closedIssues.length}/${allIssues.length} resolved`}
            color={issueCloseRate >= 80 ? '#22c55e' : '#f59e0b'}
          />
          <RingKPI
            value={MOCK_INCIDENTS.filter(i => i.status === 'CLOSED').length}
            max={Math.max(totalIncidents, 1)}
            label="Incident close rate"
            sublabel={`${MOCK_INCIDENTS.filter(i=>i.status==='CLOSED').length}/${totalIncidents} closed`}
            color="#4f46e5"
          />
        </div>
      </div>

      {/* ── SAFETY KPI SUMMARY TABLE ─────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 bg-slate-50 border-b flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 text-sm">Safety performance summary — YTD</h3>
          <button className="btn-secondary flex items-center gap-1.5 text-xs">
            <Download className="w-3 h-3" /> Export
          </button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100">
              {['KPI', 'Value', 'Target', 'Status'].map(h => (
                <th key={h} className="px-5 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {[
              { kpi: 'TRIFR (Total Recordable Injury Frequency Rate)', value: `${TRIFR}`, target: '< 15.0', ok: Number(TRIFR) < 15 },
              { kpi: 'LTIFR (Lost Time Injury Frequency Rate)', value: `${LTIFR}`, target: '< 5.0', ok: Number(LTIFR) < 5 },
              { kpi: 'Days since last LTI', value: daysSinceLast !== null ? `${daysSinceLast} days` : '—', target: '> 90 days', ok: (daysSinceLast ?? 0) > 90 },
              { kpi: 'Worker induction completion rate', value: `${totalInductions > 0 ? Math.round((passedI/totalInductions)*100) : 0}%`, target: '≥ 95%', ok: passedI/Math.max(totalInductions,1) >= 0.95 },
              { kpi: 'Inspection pass rate', value: `${totalInspections > 0 ? Math.round((passedInsp/totalInspections)*100) : 0}%`, target: '≥ 80%', ok: passedInsp/Math.max(totalInspections,1) >= 0.8 },
              { kpi: 'Issue close-out rate', value: `${issueCloseRate}%`, target: '≥ 85%', ok: issueCloseRate >= 85 },
              { kpi: 'Toolbox talks this month', value: String(talksMonth), target: '≥ 4/month', ok: talksMonth >= 4 },
              { kpi: 'Open critical/high issues', value: String((issuesBySeverity['CRITICAL'] ?? 0) + (issuesBySeverity['HIGH'] ?? 0)), target: '0', ok: ((issuesBySeverity['CRITICAL'] ?? 0) + (issuesBySeverity['HIGH'] ?? 0)) === 0 },
              { kpi: 'Permits awaiting approval', value: String(pendingPermits), target: '< 2', ok: pendingPermits < 2 },
              { kpi: 'Urgent WHS notifications YTD', value: String(urgentNotifs), target: 'Monitor', ok: true },
            ].map((row, i) => (
              <tr key={i} className={`${!row.ok ? 'bg-red-50/30' : ''}`}>
                <td className="px-5 py-2.5 text-slate-700">{row.kpi}</td>
                <td className="px-5 py-2.5 font-semibold text-slate-900 tabular-nums">{row.value}</td>
                <td className="px-5 py-2.5 text-slate-400 text-xs">{row.target}</td>
                <td className="px-5 py-2.5">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full
                    ${row.ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {row.ok ? '✓ On target' : '⚠ Action needed'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── AI INSIGHTS ─────────────────────────────────────────── */}
      <div className="space-y-3">
        <h3 className="font-semibold text-slate-900">AI-powered insights</h3>
        <p className="text-xs text-slate-400">Patterns identified from permit, inspection, incident, and induction data across all active projects.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InsightCard
            type="risk"
            title="Excavation risk concentration — TP-2026-004"
            body="100% of near-miss incidents on TP-2026-004 are excavation-related (1/1 incidents). Combined with the CONDITIONAL inspection result and 1 open HIGH severity issue, this project has a disproportionate excavation risk profile."
            action="Increase excavation check frequency to daily on TP-2026-004. Review SWMS-2026-004 controls with site crew."
          />
          <InsightCard
            type="risk"
            title="VD Civil Pty Ltd — induction gap"
            body="VD Civil has a 50% worker induction completion rate (1/2 workers inducted). One worker (Pham Van Duc) failed the company induction and has not re-inducted. Workers comp insurance expires in < 30 days. Site access should be reviewed."
            action="Schedule face-to-face re-induction for Pham Van Duc. Contact VD Civil for workers comp renewal evidence."
          />
          <InsightCard
            type="info"
            title="Permit approval bottleneck identified"
            body="PTW-2026-003 (Energised Electrical) has been in SUBMITTED status for 2+ days without approval. Energised electrical permits require immediate WHSO review due to switching programme time-sensitivity."
            action="Review PTW-2026-003 urgently. Energex switching programme SP-2026-04881 may have a scheduled outage window."
          />
          <InsightCard
            type="good"
            title="Thomson Electrical — consistent safety performer"
            body="Thomson Electrical Pty Ltd has 100% induction completion, approved SWMS on file, all compliance documents current, and 0 incidents or open issues across all engagements. No corrective actions required."
          />
          <InsightCard
            type="info"
            title="SWMS sign-on rate below threshold"
            body="Current worker SWMS sign-on data suggests not all site workers have formally acknowledged the active SWMS documents. This is a legal requirement under the WHS Regulations for all high-risk construction work."
            action="Verify all crew members have signed on to SWMS-2026-001 and SWMS-2026-004 via the SWMS module."
          />
          <InsightCard
            type="good"
            title="TRIFR tracking well below industry benchmark"
            body={`Current TRIFR of ${TRIFR} is significantly below the Australian construction industry benchmark of 15–18 per million hours worked, and LTIFR of ${LTIFR} indicates no lost-time injuries YTD.`}
          />
        </div>
      </div>
    </div>
  )
}


// ── Main ──────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────
// PHASE 6 — JOB COST REPORT (cross-module: Jobs + POs + Timesheets)
// Per-job cost roll-up: contract value, material cost, labour cost,
// margin. The single most requested PM report.
// ─────────────────────────────────────────────────────────────────
function generateJobCostReport(jobs: any[], pos: any[], timesheets: any[], payRates: Record<string,number>, employees: any[]) {
  const now    = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtAUD = fmtX

  const calcLabourCost = (jobId: string) => {
    let cost = 0
    for (const ts of timesheets) {
      if (ts.status !== 'APPROVED' && ts.status !== 'SUBMITTED') continue
      const emp  = employees.find((e: any) => e.id === ts.employeeId)
      const rate = payRates[emp?.payClassification ?? ''] ?? 0
      for (const entry of ts.entries) {
        if (entry.jobId !== jobId || !entry.start || !entry.end) continue
        const [sh,sm] = entry.start.split(':').map(Number)
        const [eh,em] = entry.end.split(':').map(Number)
        const hrs = Math.max(0, ((eh*60+em)-(sh*60+sm)-(entry.break ?? 0))/60)
        const ord = Math.min(hrs, 8), ot = Math.max(0, hrs-8)
        cost += ord*rate + ot*rate*1.5
      }
    }
    return cost
  }

  const rows = jobs.map((j: any, i: number) => {
    const jobPOs      = pos.filter((p: any) => p.jobId === j.id)
    const materialCost = jobPOs.reduce((s: number, p: any) => s + p.totalExGST, 0)
    const labourCost   = calcLabourCost(j.id)
    const totalCost    = materialCost + labourCost
    const margin       = j.contractValue - totalCost
    const marginPct    = j.contractValue > 0 ? (margin / j.contractValue * 100) : 0
    const marginColor  = marginPct >= 20 ? '#166534' : marginPct >= 10 ? '#d97706' : '#b91c1c'
    return `<tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:6px 10px;font-weight:bold">${j.jobNumber}</td>
      <td style="padding:6px 10px;font-size:9.5pt">${j.description}</td>
      <td style="padding:6px 8px;text-align:right">${fmtAUD(j.contractValue)}</td>
      <td style="padding:6px 8px;text-align:right;color:#1d4ed8">${fmtAUD(materialCost)}</td>
      <td style="padding:6px 8px;text-align:right;color:#7e22ce">${fmtAUD(labourCost)}</td>
      <td style="padding:6px 8px;text-align:right;font-weight:bold">${fmtAUD(totalCost)}</td>
      <td style="padding:6px 8px;text-align:right;font-weight:bold;color:${marginColor}">${fmtAUD(margin)}</td>
      <td style="padding:6px 8px;text-align:center;font-weight:bold;color:${marginColor}">${marginPct.toFixed(1)}%</td>
    </tr>`
  }).join('')

  const totalContract = jobs.reduce((s: number, j: any) => s + j.contractValue, 0)
  const totalMaterial = jobs.reduce((s: number, j: any) => s + pos.filter((p: any) => p.jobId === j.id).reduce((s2: number, p: any) => s2 + p.totalExGST, 0), 0)
  const totalLabour   = jobs.reduce((s: number, j: any) => s + calcLabourCost(j.id), 0)
  const totalCost     = totalMaterial + totalLabour
  const totalMargin   = totalContract - totalCost

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Job Cost Report — ${now}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:14mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:12px}
.kpi{display:flex;gap:10px;margin-bottom:12px}
.kpi-box{flex:1;border:1px solid #e5e7eb;border-radius:5px;padding:8px;text-align:center}
.kpi-v{font-size:15pt;font-weight:900}
table{width:100%;border-collapse:collapse}
th{background:#1B3A6B;color:#fff;padding:6px 8px;text-align:left;font-size:9.5pt}
.total-row{background:#1B3A6B;color:#fff;font-weight:bold}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:16px;border-top:1px solid #eee;padding-top:6px}
@media print{body{margin:10mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901</div></div>
  <div style="text-align:right"><div style="font-size:16pt;font-weight:900;color:#1B3A6B">JOB COST REPORT</div><div style="font-size:9pt;color:#888">As at: ${now}</div></div>
</div>
<div class="kpi">
  <div class="kpi-box"><div class="kpi-v" style="color:#1B3A6B">${fmtAUD(totalContract)}</div><div style="font-size:9pt;color:#6b7280">Total contract value</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#1d4ed8">${fmtAUD(totalMaterial)}</div><div style="font-size:9pt;color:#6b7280">Material cost (PO)</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#7e22ce">${fmtAUD(totalLabour)}</div><div style="font-size:9pt;color:#6b7280">Labour cost</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:${totalMargin>=0?'#166534':'#b91c1c'}">${fmtAUD(totalMargin)}</div><div style="font-size:9pt;color:#6b7280">Gross margin</div></div>
</div>
<table>
  <thead><tr><th>Job #</th><th>Description</th><th style="text-align:right">Contract value</th><th style="text-align:right">Material cost</th><th style="text-align:right">Labour cost</th><th style="text-align:right">Total cost</th><th style="text-align:right">Margin</th><th style="text-align:center">Margin %</th></tr></thead>
  <tbody>${rows}
    <tr class="total-row">
      <td colspan="2" style="padding:7px 10px">TOTAL</td>
      <td style="padding:7px 8px;text-align:right">${fmtAUD(totalContract)}</td>
      <td style="padding:7px 8px;text-align:right">${fmtAUD(totalMaterial)}</td>
      <td style="padding:7px 8px;text-align:right">${fmtAUD(totalLabour)}</td>
      <td style="padding:7px 8px;text-align:right">${fmtAUD(totalCost)}</td>
      <td style="padding:7px 8px;text-align:right">${fmtAUD(totalMargin)}</td>
      <td style="padding:7px 8px;text-align:center">${totalContract>0?(totalMargin/totalContract*100).toFixed(1):'0.0'}%</td>
    </tr>
  </tbody>
</table>
<p style="font-size:8.5pt;color:#888;margin-top:8px">Labour cost calculated from approved/submitted timesheets at award rates with 1.5x overtime above 8hrs/day. Material cost is all PO commitment (ex GST) regardless of delivery status.</p>
<div class="footer">MCB Civil Electrical · Job Cost Report · ${now} · Confidential — internal financial records</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1100,height=800')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 6 — AGED RECEIVABLES REPORT
// Progress claims certified but not yet paid, aged by days overdue.
// ─────────────────────────────────────────────────────────────────
function generateAgedReceivablesReport(claims: any[]) {
  const now    = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const today  = new Date()
  const fmtAUD = fmtX
  const fmtD   = (d: string | null) => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

  const outstanding = claims.filter(c => c.status !== 'PAID')

  const rows = outstanding.map((c: any, i: number) => {
    const due = c.paymentDueDate ? new Date(c.paymentDueDate) : null
    const daysOverdue = due ? Math.floor((today.getTime() - due.getTime()) / 86400000) : null
    const aging = daysOverdue === null ? 'Not yet due' : daysOverdue > 60 ? '61-90d' : daysOverdue > 30 ? '31-60d' : daysOverdue > 0 ? '1-30d' : 'Current'
    const agingColor = daysOverdue === null ? '#6b7280' : daysOverdue > 60 ? '#b91c1c' : daysOverdue > 30 ? '#d97706' : daysOverdue > 0 ? '#ca8a04' : '#166534'
    return `<tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:6px 10px;font-weight:bold">${c.jobNumber}</td>
      <td style="padding:6px 10px;text-align:center">Claim ${c.claimNumber}</td>
      <td style="padding:6px 10px;text-align:right;font-weight:bold">${fmtAUD(c.certifiedAmount ?? c.totalIncGST)}</td>
      <td style="padding:6px 10px;text-align:center">${fmtD(c.certifiedDate)}</td>
      <td style="padding:6px 10px;text-align:center">${fmtD(c.paymentDueDate)}</td>
      <td style="padding:6px 10px;text-align:center;font-weight:bold;color:${agingColor}">${aging}</td>
      <td style="padding:6px 10px;text-align:center;font-weight:bold;color:${daysOverdue && daysOverdue>0?'#b91c1c':'#166534'}">${daysOverdue!==null && daysOverdue>0 ? daysOverdue+'d overdue' : 'On track'}</td>
    </tr>`
  }).join('')

  const totalOutstanding = outstanding.reduce((s: number, c: any) => s + (c.certifiedAmount ?? c.totalIncGST ?? 0), 0)
  const overdueAmt = outstanding.filter((c: any) => c.paymentDueDate && today > new Date(c.paymentDueDate)).reduce((s: number, c: any) => s + (c.certifiedAmount ?? 0), 0)

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Aged Receivables — ${now}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:14mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:12px}
.kpi{display:flex;gap:10px;margin-bottom:10px}
.kpi-box{flex:1;border:1px solid #e5e7eb;border-radius:5px;padding:8px;text-align:center}
.kpi-v{font-size:15pt;font-weight:900}
table{width:100%;border-collapse:collapse}
th{background:#1B3A6B;color:#fff;padding:6px 10px;text-align:left;font-size:9.5pt}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:16px;border-top:1px solid #eee;padding-top:6px}
@media print{body{margin:10mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901</div></div>
  <div style="text-align:right"><div style="font-size:16pt;font-weight:900;color:#1B3A6B">AGED RECEIVABLES REPORT</div><div style="font-size:9pt;color:#888">As at: ${now}</div></div>
</div>
<div class="kpi">
  <div class="kpi-box"><div class="kpi-v" style="color:#1B3A6B">${fmtAUD(totalOutstanding)}</div><div style="font-size:9pt;color:#6b7280">Total outstanding</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#b91c1c">${fmtAUD(overdueAmt)}</div><div style="font-size:9pt;color:#6b7280">Overdue</div></div>
  <div class="kpi-box"><div class="kpi-v">${outstanding.length}</div><div style="font-size:9pt;color:#6b7280">Claims outstanding</div></div>
</div>
<table>
  <thead><tr><th>Job</th><th style="text-align:center">Claim</th><th style="text-align:right">Amount</th><th style="text-align:center">Certified</th><th style="text-align:center">Due date</th><th style="text-align:center">Aging</th><th style="text-align:center">Status</th></tr></thead>
  <tbody>${rows || '<tr><td colspan="7" style="padding:12px;text-align:center;color:#9ca3af">No outstanding receivables</td></tr>'}</tbody>
</table>
<div class="footer">MCB Civil Electrical · Aged Receivables Report · ${now} · Confidential</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1100,height=750')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 6 — RETENTION SCHEDULE REPORT
// All retention held per contract, with expected release date
// (practical completion + defects liability period).
// ─────────────────────────────────────────────────────────────────
function generateRetentionScheduleReport(contracts: any[]) {
  const now    = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtAUD = fmtX
  const fmtD   = (d: string | null) => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

  const rows = contracts.map((c: any, i: number) => {
    const pcDate = c.practicalCompletionActual ? new Date(c.practicalCompletionActual) : c.practicalCompletionDate ? new Date(c.practicalCompletionDate) : null
    const releaseDate = pcDate ? new Date(pcDate.getTime() + (c.defectsLiabilityMonths ?? 12) * 30.44 * 86400000) : null
    const daysToRelease = releaseDate ? Math.ceil((releaseDate.getTime() - Date.now()) / 86400000) : null
    return `<tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:6px 10px;font-weight:bold">${c.jobNumber}</td>
      <td style="padding:6px 10px;font-size:9.5pt">${c.clientName}</td>
      <td style="padding:6px 8px;text-align:right">${c.retentionPercent}%</td>
      <td style="padding:6px 8px;text-align:right;font-weight:bold;color:#7e22ce">${fmtAUD(c.retentionHeld)}</td>
      <td style="padding:6px 8px;text-align:center">${c.practicalCompletionActual ? fmtD(c.practicalCompletionActual) : (c.practicalCompletionDate ? fmtD(c.practicalCompletionDate)+' (est.)' : 'TBC')}</td>
      <td style="padding:6px 8px;text-align:center">${c.defectsLiabilityMonths ?? 12} months</td>
      <td style="padding:6px 8px;text-align:center;font-weight:bold">${releaseDate ? fmtD(releaseDate.toISOString()) : 'TBC'}</td>
      <td style="padding:6px 8px;text-align:center;font-weight:bold;color:${daysToRelease!==null && daysToRelease<90?'#d97706':'#166534'}">${daysToRelease!==null ? (daysToRelease<0?'Due now':daysToRelease+'d') : '—'}</td>
    </tr>`
  }).join('')

  const totalRetention = contracts.reduce((s: number, c: any) => s + c.retentionHeld, 0)

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Retention Schedule — ${now}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:14mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:12px}
.kpi{display:flex;gap:10px;margin-bottom:10px}
.kpi-box{flex:1;border:1px solid #e5e7eb;border-radius:5px;padding:8px;text-align:center}
.kpi-v{font-size:15pt;font-weight:900}
table{width:100%;border-collapse:collapse}
th{background:#1B3A6B;color:#fff;padding:6px 8px;text-align:left;font-size:9.5pt}
.total-row{background:#1B3A6B;color:#fff;font-weight:bold}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:16px;border-top:1px solid #eee;padding-top:6px}
@media print{body{margin:10mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901</div></div>
  <div style="text-align:right"><div style="font-size:16pt;font-weight:900;color:#1B3A6B">RETENTION SCHEDULE</div><div style="font-size:9pt;color:#888">As at: ${now}</div></div>
</div>
<div class="kpi">
  <div class="kpi-box"><div class="kpi-v" style="color:#7e22ce">${fmtAUD(totalRetention)}</div><div style="font-size:9pt;color:#6b7280">Total retention held</div></div>
  <div class="kpi-box"><div class="kpi-v">${contracts.length}</div><div style="font-size:9pt;color:#6b7280">Contracts with retention</div></div>
</div>
<table>
  <thead><tr><th>Job</th><th>Client</th><th style="text-align:right">Rate</th><th style="text-align:right">Retention (ex GST)</th><th style="text-align:center">Practical completion</th><th style="text-align:center">DLP</th><th style="text-align:center">Release date</th><th style="text-align:center">Days to release</th></tr></thead>
  <tbody>${rows}
    <tr class="total-row"><td colspan="3" style="padding:7px 10px">TOTAL</td><td style="padding:7px 8px;text-align:right">${fmtAUD(totalRetention)}</td><td colspan="4"></td></tr>
  </tbody>
</table>
<p style="font-size:8.5pt;color:#888;margin-top:8px">Release date estimated as practical completion + defects liability period (DLP). Subject to satisfactory completion of defects rectification and client release approval.</p>
<div class="footer">MCB Civil Electrical · Retention Schedule · ${now} · Confidential — internal financial records</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1100,height=750')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 6 — MONTHLY PROGRESS REPORT (formal, per job)
// Client-facing formal monthly progress report.
// ─────────────────────────────────────────────────────────────────
function generateMonthlyProgressReport(job: any, contract: any, claims: any[], variations: any[]) {
  const now     = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const mLabel  = new Date().toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })
  const fmtAUD = fmtX
  const fmtD    = (d: string | null) => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

  const jobClaims = claims.filter((c: any) => c.jobId === job.id).sort((a: any,b: any) => a.claimNumber - b.claimNumber)
  const jobVars    = variations.filter((v: any) => v.jobId === job.id)
  const latestClaim = jobClaims[jobClaims.length - 1]
  const currentValue = (contract?.originalContractValue ?? job.contractValue) + (contract?.approvedVariationsValue ?? 0)

  const varRows = jobVars.map((v: any, i: number) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:5px 8px;font-family:monospace">${v.variationNumber}</td>
      <td style="padding:5px 10px;font-size:9.5pt">${v.description}</td>
      <td style="padding:5px 8px;text-align:right">${fmtAUD(v.totalExGST)}</td>
      <td style="padding:5px 8px;text-align:center;font-weight:bold;color:${v.status==='APPROVED'?'#166534':'#d97706'}">${v.status}</td>
    </tr>`).join('')

  const claimRows = jobClaims.map((c: any, i: number) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:5px 8px;text-align:center">${c.claimNumber}</td>
      <td style="padding:5px 8px;text-align:center">${fmtD(c.claimDate)}</td>
      <td style="padding:5px 8px;text-align:right">${fmtAUD(c.workCompletedThisClaim)}</td>
      <td style="padding:5px 8px;text-align:center">${c.percentComplete}%</td>
      <td style="padding:5px 8px;text-align:center;font-weight:bold;color:${c.status==='PAID'?'#166534':c.status==='CERTIFIED'?'#1d4ed8':'#d97706'}">${c.status}</td>
    </tr>`).join('')

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Monthly Progress Report — ${job.jobNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:18mm}
h1{font-size:22pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:11pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:14px 0 8px}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:flex-end}
.progress-banner{background:#1B3A6B;color:#fff;border-radius:6px;padding:14px 20px;margin:14px 0;display:flex;justify-content:space-between;align-items:center}
.progress-bar{background:rgba(255,255,255,.2);border-radius:6px;height:14px;width:200px;overflow:hidden}
.progress-fill{background:#fff;height:100%}
.info table{width:100%;border-collapse:collapse;margin-bottom:12px}
.info td{padding:4px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:34%;font-weight:bold;color:#555;background:#f9fafb}
table.data{width:100%;border-collapse:collapse;font-size:9.5pt;margin-bottom:12px}
th{background:#1B3A6B;color:#fff;padding:5px 8px;text-align:left}
.sig-row{display:flex;gap:30px;margin-top:14px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:28px;margin:12px 0 4px}.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:18px;border-top:1px solid #eee;padding-top:6px}
@media print{body{margin:14mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901 · 14 Commerce Dr, Salisbury QLD 4107 · 07 3344 5500</div></div>
  <div style="text-align:right"><div style="font-size:9pt;color:#888">${mLabel}</div><div style="font-size:9pt;color:#888">Generated: ${now}</div></div>
</div>

<div style="font-size:16pt;font-weight:900;color:#1B3A6B;margin-bottom:4px">MONTHLY PROGRESS REPORT</div>
<div style="font-size:12pt;color:#555;margin-bottom:10px">${job.jobNumber} — ${job.description}</div>

<div class="progress-banner">
  <div><div style="font-size:9pt;opacity:.8">Overall progress</div><div style="font-size:22pt;font-weight:900">${job.progressPercent}%</div></div>
  <div class="progress-bar"><div class="progress-fill" style="width:${job.progressPercent}%"></div></div>
</div>

<div class="info"><table>
  <tr><td>Client</td><td><strong>${job.clientName ?? job.client}</strong></td></tr>
  <tr><td>Contract reference</td><td>${contract?.contractNumber ?? '—'}</td></tr>
  <tr><td>Start date</td><td>${fmtD(job.startDate)}</td></tr>
  <tr><td>Target completion</td><td>${fmtD(job.targetEnd)}</td></tr>
  <tr><td>Original contract value</td><td>${fmtAUD(contract?.originalContractValue ?? job.contractValue)}</td></tr>
  <tr><td>Approved variations</td><td>${fmtAUD(contract?.approvedVariationsValue ?? 0)}</td></tr>
  <tr><td>Current contract value</td><td><strong>${fmtAUD(currentValue)}</strong></td></tr>
  <tr><td>Crew on site</td><td>${job.crew} personnel · ${job.plantOnSite} plant items</td></tr>
</table></div>

${jobVars.length > 0 ? `<h2>Variations this period</h2>
<table class="data"><thead><tr><th>Ref</th><th>Description</th><th style="text-align:right">Value (ex GST)</th><th style="text-align:center">Status</th></tr></thead>
<tbody>${varRows}
  <tr>
    <td colspan="4" style="padding:5px 10px;text-align:right;color:#555">GST (10%)</td>
    <td style="padding:5px 8px;text-align:right;font-family:monospace">${0 /* totalCost TODO */}</td>
  </tr>
  <tr style="background:#dbeafe">
    <td colspan="4" style="padding:7px 10px;font-weight:bold;text-align:right">Total inc GST</td>
    <td style="padding:7px 8px;font-weight:bold;text-align:right;font-family:monospace">${0 /* totalCost TODO */}</td>
  </tr>

  <tr>
    <td colspan="3" style="padding:5px 10px;text-align:right;color:#555">GST (10%)</td>
    <td style="padding:5px 8px;text-align:right;font-family:monospace">${0 /* totalRetention TODO */}</td>
  </tr>
  <tr style="background:#dbeafe">
    <td colspan="3" style="padding:7px 10px;font-weight:bold;text-align:right">Total inc GST</td>
    <td style="padding:7px 8px;font-weight:bold;text-align:right;font-family:monospace">${0 /* totalRetention TODO */}</td>
  </tr>
</tbody></table>` : ''}

<h2>Progress claims history</h2>
${claimRows ? `<table class="data"><thead><tr><th style="text-align:center">Claim #</th><th style="text-align:center">Date</th><th style="text-align:right">Value this claim</th><th style="text-align:center">% complete</th><th style="text-align:center">Status</th></tr></thead>
<tbody>${claimRows}</tbody></table>` : '<p style="color:#888;font-size:10pt">No progress claims submitted yet.</p>'}

<h2>Safety &amp; quality</h2>
<div class="info"><table>
  <tr><td>Open WHS issues</td><td>${job.openIssues ?? 0}</td></tr>
  <tr><td>Open hold points</td><td>${job.openHoldPoints ?? 0}</td></tr>
  <tr><td>Open NCRs</td><td>${job.openNCRs ?? 0}</td></tr>
</table></div>

<h2>Outlook</h2>
<p style="font-size:10pt;color:#555">Works continue to progress toward the target completion date. ${job.openHoldPoints > 0 ? 'Outstanding hold points require client/superintendent attendance to avoid programme delay.' : 'No outstanding hold points are currently blocking programme progress.'} ${job.openNCRs > 0 ? `${job.openNCRs} non-conformance report(s) remain open and are being actioned.` : ''}</p>

<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Project Manager — ${job.pmName ?? job.pm} · date</div></div>
</div>
<div class="footer">MCB Civil Electrical · Monthly Progress Report · ${job.jobNumber} · ${mLabel} · Generated ${now}</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1020,height=820')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 5 — FORMAL WHS COMPLIANCE REPORT
// Comprehensive WHS report for client/regulator submission.
// Pre-filled from all platform WHS data sources.
// ─────────────────────────────────────────────────────────────────
function generateWHSComplianceReport(
  incidents: any[], toolboxTalks: any[], swmsList: any[],
  employees: any[], inspections: any[]
) {
  const now     = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtAUD = fmtX
  const monthStart = new Date(); monthStart.setDate(1)
  const prevMonth  = new Date(monthStart.getTime() - 1); prevMonth.setDate(1)
  const mLabel  = monthStart.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })

  // KPIs
  const totalHrs    = employees.length * 22 * 8  // estimated hours this month
  const ltiCount    = incidents.filter(i => i.treatmentRequired === 'HOSPITAL' || i.treatmentRequired === 'MEDICAL_TREATMENT').length
  const trifr       = totalHrs > 0 ? ((incidents.length / totalHrs) * 1_000_000).toFixed(2) : '0.00'
  const ltifr       = totalHrs > 0 ? ((ltiCount / totalHrs) * 1_000_000).toFixed(2) : '0.00'
  const openIssues  = inspections.flatMap((i: any) => i.issues ?? []).filter((i: any) => i.status !== 'CLOSED').length
  const swmsApproved = swmsList.filter(s => s.status === 'APPROVED').length
  const tbCount     = toolboxTalks.length
  const credAlerts  = employees.filter((e: any) => {
    const expKeys = ['electricalLicenceExpiry','firstAidExpiry','whsInductionExpiry']
    return expKeys.some(k => {
      const d = e.credentials?.[k]
      return d && Math.ceil((new Date(d).getTime() - Date.now()) / 86400000) <= 90
    })
  }).length

  const incidentRows = incidents.map((i: any, idx: number) => `
    <tr style="background:${idx%2===0?'#fff':'#f9fafb'}">
      <td style="padding:5px 8px;font-family:monospace">${i.incidentNumber}</td>
      <td style="padding:5px 8px">${(i.type ?? '').replace(/_/g,' ')}</td>
      <td style="padding:5px 8px;font-weight:bold;color:${({CRITICAL:'#b91c1c',HIGH:'#c2410c',MEDIUM:'#92400e',LOW:'#1d4ed8'} as Record<string,string>)[i.severity] ?? '#111'}">${i.severity}</td>
      <td style="padding:5px 8px">${i.jobNumber ?? '—'}</td>
      <td style="padding:5px 8px;font-size:9pt">${i.dateTime ? new Date(i.dateTime).toLocaleDateString('en-AU') : '—'}</td>
      <td style="padding:5px 8px;font-size:9pt">${(i.treatmentRequired ?? 'NONE').replace(/_/g,' ')}</td>
      <td style="padding:5px 8px;text-align:center;color:${i.status==='CLOSED'?'#166534':'#b45309'};font-weight:bold">${i.status?.replace(/_/g,' ')}</td>
    </tr>`).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>WHS Compliance Report — ${mLabel}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:14mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:11pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:14px 0 7px}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:14px}
.kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:14px}
.kpi{border:1px solid #e5e7eb;border-radius:5px;padding:8px;text-align:center}
.kpi-v{font-size:20pt;font-weight:900}.kpi-l{font-size:8.5pt;color:#6b7280;margin-top:2px}
table.data{width:100%;border-collapse:collapse;font-size:9.5pt;margin-bottom:12px}
th{background:#1B3A6B;color:#fff;padding:5px 8px;text-align:left}
.info table{width:100%;border-collapse:collapse;margin-bottom:10px}
.info td{padding:4px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:40%;font-weight:bold;color:#555;background:#f9fafb}
.sig-row{display:flex;gap:30px;margin-top:16px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:28px;margin:12px 0 4px}.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:16px;border-top:1px solid #eee;padding-top:6px}
.alert{background:#fee2e2;border:1px solid #fca5a5;border-radius:4px;padding:6px 12px;margin-bottom:10px;font-size:9.5pt;color:#b91c1c;font-weight:bold}
@media print{body{margin:10mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901 · 14 Commerce Dr, Salisbury QLD 4107</div></div>
  <div style="text-align:right"><div style="font-size:16pt;font-weight:900;color:#1B3A6B">WHS COMPLIANCE REPORT</div>
    <div style="font-size:11pt;color:#1B3A6B">${mLabel}</div>
    <div style="font-size:9pt;color:#888">Generated: ${now}</div></div>
</div>

${incidents.filter((i: any) => i.notifiableToWorksafe).length > 0 ? `<div class="alert">⚠ ${incidents.filter((i: any) => i.notifiableToWorksafe).length} notifiable incident(s) in this period — WorkSafe QLD notifications issued</div>` : ''}

<div class="kpi-grid">
  <div class="kpi"><div class="kpi-v" style="color:${parseInt(trifr) > 0?'#b91c1c':'#166534'}">${trifr}</div><div class="kpi-l">TRIFR<br>(per 1M hrs)</div></div>
  <div class="kpi"><div class="kpi-v" style="color:${parseInt(ltifr) > 0?'#b91c1c':'#166534'}">${ltifr}</div><div class="kpi-l">LTIFR<br>(per 1M hrs)</div></div>
  <div class="kpi"><div class="kpi-v">${incidents.length}</div><div class="kpi-l">Total incidents<br>this period</div></div>
  <div class="kpi"><div class="kpi-v" style="color:${openIssues > 0?'#d97706':'#166534'}">${openIssues}</div><div class="kpi-l">Open inspection<br>issues</div></div>
  <div class="kpi"><div class="kpi-v" style="color:#166534">${tbCount}</div><div class="kpi-l">Toolbox talks<br>conducted</div></div>
  <div class="kpi"><div class="kpi-v" style="color:#166534">${swmsApproved}</div><div class="kpi-l">SWMS approved<br>&amp; current</div></div>
  <div class="kpi"><div class="kpi-v" style="color:${credAlerts > 0?'#d97706':'#166534'}">${credAlerts}</div><div class="kpi-l">Credential<br>alerts (&lt;90d)</div></div>
  <div class="kpi"><div class="kpi-v">${employees.length}</div><div class="kpi-l">Total workforce<br>headcount</div></div>
</div>

<h2>Incident summary</h2>
${incidents.length > 0 ? `<table class="data"><thead><tr><th>Ref</th><th>Type</th><th>Severity</th><th>Job</th><th>Date</th><th>Treatment</th><th style="text-align:center">Status</th></tr></thead>
<tbody>${incidentRows}</tbody></table>` : '<p style="color:#166534;font-weight:bold">✅ No incidents recorded in this reporting period.</p>'}

<h2>WHS inspections summary</h2>
<div class="info"><table>
  <tr><td>Inspections conducted</td><td>${inspections.length}</td></tr>
  <tr><td>Pass result</td><td>${inspections.filter((i: any) => i.result === 'PASS').length} of ${inspections.length}</td></tr>
  <tr><td>Issues raised</td><td>${inspections.flatMap((i: any) => i.issues ?? []).length} total · ${openIssues} open</td></tr>
</table></div>

<h2>Training &amp; worker engagement</h2>
<div class="info"><table>
  <tr><td>Toolbox talks conducted</td><td>${tbCount}</td></tr>
  <tr><td>SWMS approved and current</td><td>${swmsApproved}</td></tr>
  <tr><td>Credential renewals due (&lt;90 days)</td><td>${credAlerts}</td></tr>
</table></div>

<h2>Declaration</h2>
<p style="font-size:10pt;color:#555">I confirm that the WHS statistics and information reported above are accurate and have been compiled in accordance with MCB Civil Electrical's WHS Management System and the reporting requirements of our contracts.</p>
<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">WHSO — name, signature &amp; date</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Project Manager — reviewed &amp; certified</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Director / authorised officer — approved for submission</div></div>
</div>
<div class="footer">MCB Civil Electrical Pty Ltd · ABN 12 345 678 901 · WHS Compliance Report · ${mLabel} · ${now}</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1100,height=820')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}
export function Reports() {
  const [tab, setTab] = useState<'overview'|'financial'|'hse'|'programme'|'whs'>('overview')

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="text-sm text-gray-500 mt-0.5">Live portfolio reports — as at 17 Jun 2026</p>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit flex-wrap">
        {([
          { key: 'overview',  label: 'Overview',  icon: BarChart2 },
          { key: 'financial', label: 'Financial',  icon: DollarSign },
          { key: 'hse',       label: 'HSE',        icon: Shield },
          { key: 'programme', label: 'Programme',  icon: Calendar },
          { key: 'whs',       label: 'WHS Analytics', icon: Shield },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {tab === 'overview'  && <OverviewTab />}
      {tab === 'financial' && <FinancialTab />}
      {tab === 'hse'       && <HSETab />}
      {tab === 'programme' && <ProgrammeTab />}
      {tab === 'whs'       && <WHSAnalyticsTab />}
    </div>
  )
}
